import React from 'react';
import { ShieldCheckIcon, MailIcon } from '@primer/octicons-react';
import SubscriptionModal from './SubscriptionModal';
import IconCircleBadge from './IconCircleBadge';

// Hard-stop gate shown when a non-admin user clicks Subscribe. Subscribing
// changes billing for the whole organization, so it's limited to admins; this
// surfaces the account owner to follow up with. It is a gate, not an
// acknowledgment — it writes nothing and reappears on every Subscribe click.
//
// Every reference to a person is gated on `owner` (org.subscription_owner, which
// is nil when the owner is deactivated and no active admin remains — UC-3 ext
// 2a2). That includes the closing paragraph: it previously rendered
// unconditionally, so an org with no contact told the user to "reach out to your
// organization's primary contact or one of the administrators" — people who by
// definition don't exist. It now explains the situation instead. Scenario 11
// covers this state and did not catch it: the scenario only asserted that no
// contact was surfaced, which was already true.
//
// Design: AdminGateModal in Prototype C_A_primer_v2.html (shield badge →
// "ADMIN ACCESS REQUIRED" → "Only admins can subscribe" → owner callout →
// "Back to dashboard" + "Email {owner}"). Built on the shared SubscriptionModal
// shell, matching the ApplicationSubmittedModal badge/body recipe.
//
// Props: { isOpen, onClose, org } — owner identity comes from
// org.subscription_owner ({ name, email }).
export default function AdminGateModal({ isOpen, onClose, org }) {
  if (!isOpen) return null;

  const owner = org?.subscription_owner;
  const orgName = org?.name || 'your organization';
  const initials = owner?.name
    ? owner.name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0])
        .join('')
        .toUpperCase()
    : '';

  const badge = <IconCircleBadge icon={ShieldCheckIcon} />;

  const body = (
    <>
      <p
        style={{
          margin: '14px auto 0',
          maxWidth: 410,
          fontSize: 15,
          lineHeight: 1.6,
          color: 'var(--fgColor-muted)',
        }}
      >
        Subscribing changes billing for all of{' '}
        <strong style={{ color: 'var(--fgColor-default)' }}>{orgName}</strong>, so
        it&rsquo;s limited to organization administrators. Your account
        doesn&rsquo;t have admin access.
      </p>

      {owner && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            margin: '18px auto 0',
            maxWidth: 360,
            background: 'var(--bgColor-muted)',
            border: '1px solid var(--borderColor-muted)',
            borderRadius: 8,
            padding: '12px 14px',
            textAlign: 'left',
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'var(--fgColor-accent)',
              color: 'var(--fgColor-onEmphasis)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 14,
              flexShrink: 0,
            }}
          >
            {initials}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fgColor-default)' }}>
              {owner.name}
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--fgColor-muted)',
                overflowWrap: 'anywhere',
                marginTop: 2,
              }}
            >
              {owner.email}
            </div>
            <span
              style={{
                display: 'inline-block',
                marginTop: 6,
                fontSize: 10.5,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'var(--fgColor-accent)',
                background: 'var(--bgColor-accent-muted)',
                borderRadius: 999,
                padding: '2px 8px',
                whiteSpace: 'nowrap',
              }}
            >
              Primary contact
            </span>
          </div>
        </div>
      )}

      <p
        style={{
          margin: '16px auto 0',
          maxWidth: 410,
          fontSize: 14,
          lineHeight: 1.6,
          color: 'var(--fgColor-muted)',
        }}
      >
        {owner ? (
          <>
            To subscribe, reach out to your organization&rsquo;s primary contact or one
            of the administrators &mdash; they can subscribe{' '}
            {orgName} in a couple of clicks.
          </>
        ) : (
          <>
            {orgName} doesn&rsquo;t currently have an active administrator, so there
            isn&rsquo;t anyone to send you to.
          </>
        )}
      </p>
    </>
  );

  const footer = owner && (
    <a
      href={`mailto:${owner.email}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        color: 'var(--fgColor-accent)',
        fontSize: 13,
        fontWeight: 500,
        textDecoration: 'none',
      }}
    >
      <MailIcon size={14} />
      Email {owner.name}
    </a>
  );

  return (
    <SubscriptionModal
      isOpen={isOpen}
      badge={badge}
      eyebrow="ADMIN ACCESS REQUIRED"
      heading="Only admins can subscribe"
      primaryLabel="Back to dashboard"
      onPrimary={onClose}
      footer={footer}
    >
      {body}
    </SubscriptionModal>
  );
}
